// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- pi-lens/Deno resolver false positives on Expo/RN workspace imports in this shared-link component; repo-native toolchain resolves them correctly
// @ts-nocheck
import React, { useEffect } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import Animated, { SlideInUp } from "react-native-reanimated";

// Components
import { CrisisBanner } from "../safety/CrisisBanner";
import { ModernResultsPreview, ModernResultsFull } from "../modern-results";
import { ErrorBoundary } from "../ErrorBoundary";
import { OfflineBanner } from "../OfflineBanner";
import { ShareCopyLinkButton } from "./ShareCopyLinkButton";

// Analytics
import { captureEvent } from "../../lib/analytics";
import { useResultsExperiencePolicy } from "../../hooks/useResultsExperiencePolicy";

// Types
import type { SharedLinkExperiencePolicyOutput } from "../../../../packages/shared/src/types/screen-experience";
import type { QuotaTier } from "../trial/QuotaExhaustedModal";

interface SharedLinkContentProps {
	analysis: any;
	currentTier: QuotaTier;
	displayTier: QuotaTier;
	tierLevel: number;
	l3AccordionsEnabled: boolean;
	bottomPadding: number;
	shortId: string;
	onNewAnalysis: () => void;
	onUpgrade: () => void;
	t: any;
}

/**
 * Main content component for shared links
 */
export function SharedLinkContent({
	analysis,
	currentTier,
	displayTier,
	tierLevel,
	l3AccordionsEnabled,
	bottomPadding,
	shortId,
	onNewAnalysis,
	onUpgrade,
	t,
}: SharedLinkContentProps) {
	void React;
	const sharedResultsPolicy = useResultsExperiencePolicy({
		surface: "shared_link",
		accessToken: null,
		userId: null,
		isAnonymous: currentTier === "anonymous",
		isRealUser: currentTier !== "anonymous",
		authLoading: false,
		tier: displayTier,
		tierLevel: Math.min(3, Math.max(1, tierLevel)) as 1 | 2 | 3,
		visualLayer:
			tierLevel >= 3 ? "layer3" : tierLevel === 2 ? "layer2" : "layer1",
		source:
			currentTier === "family_member"
				? "family"
				: currentTier === "anonymous"
					? "anonymous"
					: "individual",
		isPremium: tierLevel >= 2,
		isEntitlementsLoading: false,
		analysisCount: null,
		isTrialStateLoaded: true,
		analysis,
		hasSharedLinkPayload: true,
	}) as SharedLinkExperiencePolicyOutput;

	// Track page view for shared links
	useEffect(() => {
		if (analysis) {
			captureEvent("shared_analysis_view", {
				analysis_id: analysis.id,
				viewer_tier: currentTier,
				short_id: shortId,
				source: "short_url",
			});
		}
	}, [analysis, currentTier, shortId]);

	return (
		<ErrorBoundary componentName="SharedLinkContent">
			<View style={styles.container}>
				{/* Y2K Gradient Background */}
				<LinearGradient
					colors={["#0a0012", "#1a0a2e", "#0d1117"]}
					style={StyleSheet.absoluteFill}
					start={{ x: 0, y: 0 }}
					end={{ x: 1, y: 1 }}
				/>

				{/* Offline Banner */}
				<OfflineBanner />

				<Animated.View
					entering={SlideInUp.springify().damping(15)}
					style={styles.resultsContainer}
				>
					<ScrollView
						style={styles.flex}
						contentContainerStyle={[
							styles.resultsContent,
							{ paddingBottom: bottomPadding },
						]}
					>
						{/* Crisis Resources Banner */}
						{analysis.safety?.showResources && (
							<CrisisBanner
								crisisDetected={analysis.safety.crisisDetected}
								severity={analysis.safety.severity}
								moderationEventId={analysis.safety.moderationEventId}
								screen="results"
								testID="crisis-banner-short-url"
							/>
						)}

						{/* Share Your Results Button */}
						<View style={styles.shareButtonContainer}>
							<ShareCopyLinkButton
								shortId={shortId}
								testID="share-copy-link-button"
							/>
						</View>

						{/* Tier-based conditional rendering (viewer's tier, not creator's tier) */}
						{sharedResultsPolicy.renderVariant === "preview" ? (
							// L1 Tiers: ModernResultsPreview
							<ModernResultsPreview
								analysis={analysis}
								message={analysis.message || ""}
								tier={displayTier}
								onNewAnalysis={onNewAnalysis}
								onUpgrade={onUpgrade}
								t={t}
								hideShareButton
								analyticsSurface="shared_link"
							/>
						) : (
							// L2/L3 Tiers: ModernResultsFull
							<ModernResultsFull
								analysis={analysis}
								message={analysis.message || ""}
								context={analysis.context}
								tierLevel={tierLevel}
								tier={displayTier}
								onNewAnalysis={onNewAnalysis}
								onUpgrade={onUpgrade}
								l3AccordionsEnabled={l3AccordionsEnabled}
								t={t}
								hideShareButton
								analyticsSurface="shared_link"
							/>
						)}
					</ScrollView>
				</Animated.View>
			</View>
		</ErrorBoundary>
	);
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
	},
	flex: {
		flex: 1,
	},
	resultsContainer: {
		flex: 1,
	},
	resultsContent: {
		padding: 24,
		// Note: paddingBottom is applied inline via useResponsiveScreenPadding()
	},
	shareButtonContainer: {
		marginBottom: 24,
		alignItems: "center",
	},
});
